import { useState } from 'react'
import type { SessionTypes } from '@walletconnect/types'
import { Trash2 } from 'lucide-react'
import { pair, disconnectSession, resetAllSessions } from '../lib/walletkit.js'
import { MAIN_SAFE } from '../lib/constants.js'
import { short } from '../decode.js'
import { usePrivacy } from '../lib/privacy.js'
import { Modal } from './Modal.js'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

interface Props {
  open: boolean
  onClose: () => void
  sessions: SessionTypes.Struct[]
  onRefresh: () => void
}

export function DAppsModal({ open, onClose, sessions, onRefresh }: Props) {
  const [uri, setUri] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  usePrivacy()

  const onPair = async () => {
    if (!uri.trim()) return
    setBusy(true)
    setErr(null)
    try {
      await pair(uri.trim())
      setUri('')
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const onDisconnect = async (topic: string) => {
    await disconnectSession(topic)
    onRefresh()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="🔗 Connect dApps"
      headerExtra={
        <Badge variant="outline" className="font-mono">
          advertising {short(MAIN_SAFE)}
        </Badge>
      }
    >
      <p className="text-sm text-muted-foreground">
        Open a dApp (Morpho, Pendle, app.safe.global), choose
        “WalletConnect”, and paste the wc:// URI here.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="wc-uri">Pair a new dApp</Label>
        <Input
          id="wc-uri"
          value={uri}
          onChange={(e) => setUri(e.target.value)}
          placeholder="wc:abc123…@2?relay-protocol=irn&symKey=…"
          autoFocus
          className="font-mono"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => void onPair()} disabled={busy || !uri}>
          {busy ? 'Pairing…' : 'Pair dApp'}
        </Button>
        <Button
          variant="ghost"
          onClick={async () => {
            if (!confirm('Disconnect every dApp and clear WalletKit storage?')) return
            await resetAllSessions()
            window.location.reload()
          }}
          title="Use this if you see 'No matching key' errors"
        >
          <Trash2 />
          Reset sessions
        </Button>
        {err && <span className="text-sm text-destructive">{err}</span>}
      </div>

      {sessions.length > 0 && (
        <>
          <Separator />
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Active sessions ({sessions.length})
          </p>
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li
                key={s.topic}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="font-medium">
                    {s.peer.metadata.name || 'unknown dApp'}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {s.peer.metadata.url}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void onDisconnect(s.topic)}
                >
                  Disconnect
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  )
}
